/**
 * Constantes del sistema
 * Define roles, estados, tipos y formatos utilizados en toda la aplicación
 */

// URL base del API
export const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Roles del sistema
export const ROLES = {
  ADMINISTRADOR: 'administrador',
  CAJERO: 'cajero',
  CONTADOR: 'contador',
  AUDITOR: 'auditor',
};

// Labels de roles
export const ROLE_LABELS = {
  [ROLES.ADMINISTRADOR]: 'Administrador',
  [ROLES.CAJERO]: 'Cajero',
  [ROLES.CONTADOR]: 'Contador',
  [ROLES.AUDITOR]: 'Auditor',
};

// Estados de cheques
export const CHECK_STATES = {
  PENDIENTE: 'PENDIENTE',
  EN_CARTERA: 'EN_CARTERA',
  DEPOSITADO: 'DEPOSITADO',
  COBRADO: 'COBRADO',
  RECHAZADO: 'RECHAZADO',
  ANULADO: 'ANULADO',
  DEVUELTO: 'DEVUELTO',
};

// Labels de estados de cheques
export const CHECK_STATE_LABELS = {
  [CHECK_STATES.PENDIENTE]: 'Pendiente',
  [CHECK_STATES.EN_CARTERA]: 'En Cartera',
  [CHECK_STATES.DEPOSITADO]: 'Depositado',
  [CHECK_STATES.COBRADO]: 'Cobrado',
  [CHECK_STATES.RECHAZADO]: 'Rechazado',
  [CHECK_STATES.ANULADO]: 'Anulado',
  [CHECK_STATES.DEVUELTO]: 'Devuelto',
};

// Colores de estados de cheques
export const CHECK_STATE_COLORS = {
  [CHECK_STATES.PENDIENTE]: 'warning',
  [CHECK_STATES.EN_CARTERA]: 'info',
  [CHECK_STATES.DEPOSITADO]: 'primary',
  [CHECK_STATES.COBRADO]: 'success',
  [CHECK_STATES.RECHAZADO]: 'error',
  [CHECK_STATES.ANULADO]: 'default',
  [CHECK_STATES.DEVUELTO]: 'secondary',
};

// Tipos de cuenta bancaria
export const ACCOUNT_TYPES = {
  AHORROS: 'AHORROS',
  CORRIENTE: 'CORRIENTE',
  CHEQUES: 'CHEQUES',
};

// Labels de tipos de cuenta
export const ACCOUNT_TYPE_LABELS = {
  [ACCOUNT_TYPES.AHORROS]: 'Ahorros',
  [ACCOUNT_TYPES.CORRIENTE]: 'Corriente',
  [ACCOUNT_TYPES.CHEQUES]: 'Cheques',
};

// Estados de depósitos
export const DEPOSIT_STATES = {
  BORRADOR: 'BORRADOR',
  CONFIRMADO: 'CONFIRMADO',
  PROCESADO: 'PROCESADO',
  RECHAZADO: 'RECHAZADO',
  ANULADO: 'ANULADO',
};

// Labels de estados de depósitos
export const DEPOSIT_STATE_LABELS = {
  [DEPOSIT_STATES.BORRADOR]: 'Borrador',
  [DEPOSIT_STATES.CONFIRMADO]: 'Confirmado',
  [DEPOSIT_STATES.PROCESADO]: 'Procesado',
  [DEPOSIT_STATES.RECHAZADO]: 'Rechazado',
  [DEPOSIT_STATES.ANULADO]: 'Anulado',
};

// Colores de estados de depósitos
export const DEPOSIT_STATE_COLORS = {
  [DEPOSIT_STATES.BORRADOR]: 'default',
  [DEPOSIT_STATES.CONFIRMADO]: 'info',
  [DEPOSIT_STATES.PROCESADO]: 'success',
  [DEPOSIT_STATES.RECHAZADO]: 'error',
  [DEPOSIT_STATES.ANULADO]: 'warning',
};

// Tipos de novedad
export const NOVEDAD_TYPES = {
  RECHAZO: 'RECHAZO',
  DEVOLUCION: 'DEVOLUCION',
  NOTA_CREDITO: 'NOTA_CREDITO',
  NOTA_DEBITO: 'NOTA_DEBITO',
  OBSERVACION: 'OBSERVACION',
};

// Labels de tipos de novedad
export const NOVEDAD_TYPE_LABELS = {
  [NOVEDAD_TYPES.RECHAZO]: 'Rechazo',
  [NOVEDAD_TYPES.DEVOLUCION]: 'Devolución',
  [NOVEDAD_TYPES.NOTA_CREDITO]: 'Nota de Crédito',
  [NOVEDAD_TYPES.NOTA_DEBITO]: 'Nota de Débito',
  [NOVEDAD_TYPES.OBSERVACION]: 'Observación',
};

// Estados de novedad
export const NOVEDAD_STATES = {
  PENDIENTE: 'PENDIENTE',
  EN_PROCESO: 'EN_PROCESO',
  RESUELTA: 'RESUELTA',
  CERRADA: 'CERRADA',
};

// Labels de estados de novedad
export const NOVEDAD_STATE_LABELS = {
  [NOVEDAD_STATES.PENDIENTE]: 'Pendiente',
  [NOVEDAD_STATES.EN_PROCESO]: 'En Proceso',
  [NOVEDAD_STATES.RESUELTA]: 'Resuelta',
  [NOVEDAD_STATES.CERRADA]: 'Cerrada',
};

// Tipos de acción de auditoría
export const AUDIT_ACTIONS = {
  CREATE: 'CREATE',
  UPDATE: 'UPDATE',
  DELETE: 'DELETE',
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  ESTADO: 'CAMBIO_ESTADO',
  FECHA: 'CAMBIO_FECHA',
};

// Labels de acciones de auditoría
export const AUDIT_ACTION_LABELS = {
  [AUDIT_ACTIONS.CREATE]: 'Creación',
  [AUDIT_ACTIONS.UPDATE]: 'Actualización',
  [AUDIT_ACTIONS.DELETE]: 'Eliminación',
  [AUDIT_ACTIONS.LOGIN]: 'Inicio de Sesión',
  [AUDIT_ACTIONS.LOGOUT]: 'Cierre de Sesión',
  [AUDIT_ACTIONS.ESTADO]: 'Cambio de Estado',
  [AUDIT_ACTIONS.FECHA]: 'Cambio de Fecha',
};

// Tablas afectadas en auditoría
export const AUDIT_TABLES = {
  CHEQUE: 'CHEQUE',
  DEPOSITO: 'DEPOSITO',
  CLIENTE: 'CLIENTE',
  BANCO: 'BANCO',
  CUENTA_BANCARIA: 'CUENTA_BANCARIA',
  USUARIO: 'USUARIO',
  NOVEDAD: 'NOVEDAD',
  CAMBIO_FECHA: 'CAMBIO_FECHA',
};

// Labels de tablas
export const AUDIT_TABLE_LABELS = {
  [AUDIT_TABLES.CHEQUE]: 'Cheque',
  [AUDIT_TABLES.DEPOSITO]: 'Depósito',
  [AUDIT_TABLES.CLIENTE]: 'Cliente',
  [AUDIT_TABLES.BANCO]: 'Banco',
  [AUDIT_TABLES.CUENTA_BANCARIA]: 'Cuenta Bancaria',
  [AUDIT_TABLES.USUARIO]: 'Usuario',
  [AUDIT_TABLES.NOVEDAD]: 'Novedad',
  [AUDIT_TABLES.CAMBIO_FECHA]: 'Cambio de Fecha',
};

// Formatos de fecha
export const DATE_FORMATS = {
  SHORT: 'dd/MM/yyyy',
  LONG: 'dd/MM/yyyy HH:mm:ss',
  MONTH_YEAR: 'MMMM yyyy',
  DAY_MONTH: 'dd MMM',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
  DATE_ONLY: 'yyyy-MM-dd',
  TIME_ONLY: 'HH:mm:ss',
};

// Monedas
export const CURRENCIES = {
  USD: 'USD',
  VES: 'VES',
  EUR: 'EUR',
  ARS: 'ARS',
};

// Labels de monedas
export const CURRENCY_LABELS = {
  [CURRENCIES.USD]: 'Dólar',
  [CURRENCIES.VES]: 'Bolívar',
  [CURRENCIES.EUR]: 'Euro',
  [CURRENCIES.ARS]: 'Peso Argentino',
};

// Símbolos de monedas
export const CURRENCY_SYMBOLS = {
  [CURRENCIES.USD]: '$',
  [CURRENCIES.VES]: 'Bs.',
  [CURRENCIES.EUR]: '€',
  [CURRENCIES.ARS]: '$',
};

// Tamaños de página para paginación
export const PAGE_SIZES = [10, 25, 50, 100];

// Límite de registros por defecto
export const DEFAULT_PAGE_SIZE = 25;

// Máximo de registros a exportar
export const MAX_EXPORT_RECORDS = 10000;

// Tiempos de sesión
export const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutos en milisegundos
export const TOKEN_REFRESH_INTERVAL = 15 * 60 * 1000; // 15 minutos

// Tamaños de archivo
export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
export const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];

// Rangos de fechas predefinidos
export const DATE_RANGES = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  THIS_YEAR: 'this_year',
  CUSTOM: 'custom',
};

// Labels de rangos de fechas
export const DATE_RANGE_LABELS = {
  [DATE_RANGES.TODAY]: 'Hoy',
  [DATE_RANGES.YESTERDAY]: 'Ayer',
  [DATE_RANGES.LAST_7_DAYS]: 'Últimos 7 días',
  [DATE_RANGES.LAST_30_DAYS]: 'Últimos 30 días',
  [DATE_RANGES.THIS_MONTH]: 'Este mes',
  [DATE_RANGES.LAST_MONTH]: 'Mes pasado',
  [DATE_RANGES.THIS_YEAR]: 'Este año',
  [DATE_RANGES.CUSTOM]: 'Personalizado',
};

// Días de alerta para vencimientos
export const ALERT_DAYS = {
  URGENT: 3,
  WARNING: 7,
  INFO: 15,
};

export default {
  API_BASE_URL,
  ROLES,
  ROLE_LABELS,
  CHECK_STATES,
  CHECK_STATE_LABELS,
  CHECK_STATE_COLORS,
  ACCOUNT_TYPES,
  ACCOUNT_TYPE_LABELS,
  DEPOSIT_STATES,
  DEPOSIT_STATE_LABELS,
  DEPOSIT_STATE_COLORS,
  NOVEDAD_TYPES,
  NOVEDAD_TYPE_LABELS,
  NOVEDAD_STATES,
  NOVEDAD_STATE_LABELS,
  AUDIT_ACTIONS,
  AUDIT_ACTION_LABELS,
  AUDIT_TABLES,
  AUDIT_TABLE_LABELS,
  DATE_FORMATS,
  CURRENCIES,
  CURRENCY_LABELS,
  CURRENCY_SYMBOLS,
  PAGE_SIZES,
  DEFAULT_PAGE_SIZE,
  MAX_EXPORT_RECORDS,
  SESSION_TIMEOUT,
  TOKEN_REFRESH_INTERVAL,
  MAX_FILE_SIZE,
  ALLOWED_FILE_TYPES,
  DATE_RANGES,
  DATE_RANGE_LABELS,
  ALERT_DAYS,
};
